import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import studentService from '../../services/api/student';
import StudentCourseCard from '../../components/cards/StudentCourseCard';
import { 
  Trophy,
  Layout,
  ArrowLeft,
  AlertCircle,
  GraduationCap,
  BookOpen,
  Play

} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  getLessonDuration,
  formatDurationDisplay
} from '../../utils/videoUtils';

const StudentCourses = () => {
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(6);
  

  // دالة حساب إجمالي مدة الكورس (مستندة إلى videoUtils) - مزيج من منطق المدرس والطالب
  const calculateTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return 0;
      
      // أولوية: استخدام total_duration_seconds من API (محسوبة من مجموع مدة الدروس)
      if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {
        return parseInt(course.total_duration_seconds) / 60; // تحويل من ثواني لدقائق
      }
      
      // ثانوية: استخدام حقول المدة المباشرة من الكورس (duration_hours, duration_minutes)
      if (course.duration_hours || course.duration_minutes) {
        const hours = parseInt(course.duration_hours) || 0;
        const minutes = parseInt(course.duration_minutes) || 0;
        return (hours * 60) + minutes; // تحويل لدقائق
      }
      
      // ثالثية: استخدام حقل duration_seconds إذا وجد
      if (course.duration_seconds) {
        return course.duration_seconds / 60; // تحويل من ثواني لدقائق
      }
      
      // رابعة: حساب من الدروس إذا وجدت
      if (course.lessons && course.lessons.length > 0) {
        const totalMinutes = course.lessons.reduce((total, lesson) => {
          return total + (getLessonDuration(lesson) || 0);
        }, 0);
        return totalMinutes;
      }
      
      return 0;
    };
  }, []);

  // دالة لتنسيق عرض المدة الإجمالية - نفس المنطق من صفحة المدرس
  const formatTotalDuration = useMemo(() => {
    return (course) => {
      if (!course) return '--:--';
      const totalMinutes = calculateTotalCourseDuration(course);
      return formatDurationDisplay(totalMinutes * 60); // تحويل لدقائق للثواني
    };
  }, [calculateTotalCourseDuration]);

  // جلب بيانات كورسات الطالب
  const { data: coursesData, isLoading, error } = useQuery(
    'studentCourses',
    () => studentService.getEnrolledCourses().then(data => {
      return data;
    }),
    {
      retry: 1,
    }
  );

  // منطق الفلترة (كامل وبدون اختصار)
  const filteredCourses = coursesData?.courses?.filter(course => {
    if (filter === 'all') return true;
    if (filter === 'active') return course.enrollment_status === 'active';
    if (filter === 'completed') return course.enrollment_status === 'completed';
    return true;
  }) || [];

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses?.slice(indexOfFirstCourse, indexOfLastCourse);
  const totalPages = Math.ceil((filteredCourses?.length || 0) / coursesPerPage);

  // Note: do not fetch per-lesson analytics for students (403). Use lesson.views directly.

  // Reset page when filters change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحضير قائمة كورساتك...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-16 px-6 text-center">
        <div className="bg-red-50 p-8 rounded-[2rem] border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-900 mb-2">عذراً، حدث خطأ ما</h2>
          <p className="text-gray-600 mb-6">فشلنا في تحميل قائمة الكورسات الخاصة بك. يرجى التحقق من اتصالك بالإنترنت.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-100"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">مكتبة كورساتي</h1>
          <p className="text-gray-500 mt-1 font-medium flex items-center gap-2">
            <GraduationCap size={18} className="text-primary-600" />
            تتبع تقدمك الدراسي وأكمل دروسك بانتظام للوصول للقمة
          </p>
        </div>
        <Link 
          to="/student/browse" 
          className="flex items-center gap-2 text-primary-600 font-bold bg-primary-50 px-6 py-3 rounded-2xl hover:bg-primary-100 transition-all"
        >
          استكشاف كورسات جديدة <ArrowLeft size={18} />
        </Link>
      </div>

      {/* no analytics toggle for students */}

      {/* Stats Cards Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-primary-50 text-primary-600 rounded-2xl group-hover:scale-110 transition-transform">
            <BookOpen size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">إجمالي الاشتراكات</p>
            <p className="text-3xl font-black text-gray-900">{coursesData?.courses?.length || 0}</p>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-green-50 text-green-600 rounded-2xl group-hover:scale-110 transition-transform">
            <Play size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">كورسات تدرسها الآن</p>
            <p className="text-3xl font-black text-gray-900">
              {coursesData?.courses?.filter(c => c.enrollment_status === 'active').length || 0}
            </p>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl group-hover:scale-110 transition-transform">
            <Trophy size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">كورسات تم إكمالها</p>
            <p className="text-3xl font-black text-gray-900">
              {coursesData?.courses?.filter(c => c.enrollment_status === 'completed').length || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Modern Filter Tabs */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 w-fit">
        <button
          onClick={() => setFilter('all')}
          className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${
            filter === 'all'
              ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          الكل
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${
            filter === 'active'
              ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          النشطة
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${
            filter === 'completed'
              ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          المكتملة
        </button>
      </div>

      {/* Courses Grid Section */}
      {filteredCourses.length === 0 ? (
        <div className="bg-white rounded-[3rem] p-16 text-center border border-dashed border-gray-200">
          <div className="bg-gray-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
            <Layout className="w-12 h-12 text-gray-300" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد كورسات في هذا القسم</h3>
          <p className="text-gray-500 max-w-sm mx-auto mb-8">
            {filter === 'all' 
              ? 'يبدو أنك لم تشترك في أي كورس تعليمي بعد. ابدأ رحلتك الآن واكتشف مئات الكورسات المتميزة.'
              : `لم تقم بإنهاء أي كورس في قسم "${filter === 'active' ? 'النشطة' : 'المكتملة'}" بعد.`
            }
          </p>
          <Link 
            to="/student/browse" 
            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all inline-block"
          >
            تصفح الكورسات المتاحة
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentCourses.map((course) => (
            (() => {
              const vids = (course.lessons || []);
              const courseViews = vids.reduce((sum, v) => sum + (parseInt(v?.views || v?.video_views || v?.view_count || 0, 10) || 0), 0);
              return (
                <StudentCourseCard 
                  key={course.id}
                  course={{ ...course, courseViews }}
                  formatTotalDuration={formatTotalDuration}
                  getDurationTextFromDays={() => 'اشتراك شهرياً'}
                />
              );
            })()
          ))}
        </div>
      )}
      {/* no debug panel for students */}
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            السابق
          </button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`w-10 h-10 rounded-xl font-medium transition-all ${
                  currentPage === index + 1
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            التالي
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentCourses;